import { useEffect } from "react";
import { doc } from "firebase/firestore";
import { useDocument } from "react-firebase-hooks/firestore";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  PointsActionPurchaseCompleted,
  PointsActionRefundCompleted,
} from "@t3/shared";
import { firestore } from "@/firebase";
import { cartConverter } from "@/converters";
import Link from "@/components/link";
import { useAuthContext } from "@/context/AuthContext";
import { ProductItem } from "./ProductItem";
import { getRefundProductDetails } from "../utils/refundUtils";
import { BaseTransactionCard } from "./BaseTransactionCard";
import {
  DateDisplay,
  PointsBadge,
  SectionDivider,
} from "./TransactionCardComponents";

interface PurchaseRefundCardProps {
  action: PointsActionPurchaseCompleted | PointsActionRefundCompleted;
  createdAt: string;
}

export const PurchaseRefundCard = ({
  action,
  createdAt,
}: PurchaseRefundCardProps) => {
  const typeMeta = action.typeMeta;
  const { user } = useAuthContext();
  const isRefund = action.type === "refund";

  const cartRef = doc(
    firestore,
    `company/${user.company}/users/${typeMeta.userId}/cart/${typeMeta.cartId}`,
  ).withConverter(cartConverter);

  const [cartSnapshot, loadingCart, cartError] = useDocument(cartRef);
  const cart = cartSnapshot?.data() || null;

  useEffect(() => {
    if (cartError) {
      console.error("Error fetching cart:", cartError);
    }
  }, [cartError]);

  // Calculate points from cart purchase results (matching CartBalanceSheet logic)
  const calculatePointsFromCart = (): number | null => {
    if (!cart?.purchaseResults) return null;

    let totalPointsCost = 0;
    let totalPointsRefunded = 0;

    const { tangoPurchaseResults, travelPurchaseResults, zincPurchaseResults } =
      cart.purchaseResults;

    if (tangoPurchaseResults) {
      Object.values(tangoPurchaseResults).forEach((productArray) => {
        productArray.forEach((product) => {
          if (product.status === "completed") {
            totalPointsCost += product.pointsCost;
          } else if (
            product.status === "refund" ||
            product.status === "refunded"
          ) {
            totalPointsRefunded += product.pointsRefunded;
          }
        });
      });
    }

    if (travelPurchaseResults) {
      Object.values(travelPurchaseResults).forEach((productArray) => {
        productArray.forEach((product) => {
          if (product.status === "completed") {
            totalPointsCost += product.pointsCost;
          } else if (
            product.status === "refund" ||
            product.status === "refunded"
          ) {
            totalPointsRefunded += product.pointsRefunded;
          }
        });
      });
    }

    if (zincPurchaseResults) {
      zincPurchaseResults.forEach((product) => {
        if (product.status === "completed") {
          totalPointsCost += product.pointsCost;
        } else if (
          product.status === "refund" ||
          product.status === "refunded"
        ) {
          totalPointsRefunded += product.pointsRefunded;
        }
      });
    }

    // Return refunded points for refunds, total cost for purchases
    return isRefund ? totalPointsRefunded : totalPointsCost;
  };

  // Always use cart data for points display
  const pointsDisplay = calculatePointsFromCart();

  let images: string[] = [];
  let products: Array<{
    name: string;
    image?: string;
    type?: string;
    status?: string;
  }> = [];

  if (cart?.purchaseResults) {
    const { tangoPurchaseResults, travelPurchaseResults, zincPurchaseResults } =
      cart.purchaseResults;

    if (zincPurchaseResults && zincPurchaseResults.length > 0) {
      zincPurchaseResults.forEach((result) => {
        result.zincProducts.forEach((product) => {
          if (product.image) images.push(product.image);

          // Determine status based on orderData (matching CartHistoryZincItem logic)
          let displayStatus: string;

          if (!result.orderData) {
            // No orderData means still processing
            displayStatus = "processing";
          } else if (result.orderData.responseType === "error") {
            // Error response
            displayStatus = "error";
          } else if (result.orderData.responseType === "order_response") {
            // Check for delivery tracking status first
            const trackingForProduct = result.orderData.trackingInfo?.find(
              (tracking) => tracking.product_ids.includes(product.product_id),
            );
            displayStatus =
              trackingForProduct?.delivery_status || "order placed";
          } else {
            // Fallback to purchase result status
            displayStatus = result.status;
          }

          products.push({
            name: product.title || "",
            image: product.image,
            type: "zinc",
            status: displayStatus,
          });
        });
      });
    }

    if (tangoPurchaseResults) {
      Object.values(tangoPurchaseResults).forEach((results) => {
        results.forEach((result) => {
          const imageUrl =
            result.tangoProduct?.metadata?.imageUrls?.["300w-326ppi"] ||
            result.tangoProduct?.metadata?.imageUrls?.["1200w-326ppi"] ||
            "";
          if (imageUrl) images.push(imageUrl);
          const brandName = result.tangoProduct?.metadata?.brandName || "";
          const amount = result.tangoProduct?.amount;
          const displayName = amount ? `$${amount} - ${brandName}` : brandName;
          products.push({
            name: displayName,
            image: imageUrl,
            type: "tango",
            status: result.status,
          });
        });
      });
    }

    if (travelPurchaseResults) {
      Object.values(travelPurchaseResults).forEach((results) => {
        results.forEach((result) => {
          const imageUrl =
            result.travelProduct?.metadata?.imageUrls?.firebaseImage || "";
          if (imageUrl) images.push(imageUrl);
          const brandName = result.travelProduct?.metadata?.brandName || "";
          const amount = result.travelProduct?.amount;
          const displayName = amount ? `$${amount} - ${brandName}` : brandName;
          products.push({
            name: displayName,
            image: imageUrl,
            type: "travel",
            status: result.status,
          });
        });
      });
    }
  } else if (isRefund) {
    const extracted = getRefundProductDetails(action);
    images = extracted.images;
    products = extracted.products;
  }

  const cartId =
    action.type === "purchase" || action.type === "refund"
      ? typeMeta.cartId
      : undefined;

  return (
    <Link
      to={`/shop/history/${cartId}`}
      data-cypress="history-purchase-card"
      sx={{
        textDecoration: "none",
        color: "inherit",
        transition: "all 0.2s ease-in-out",
        "&:hover": { transform: "translateY(-2px)" },
        cursor: "pointer",
      }}
    >
      <BaseTransactionCard dataCypress="history-purchase-card-inner">
        {loadingCart ? (
          <Box display="flex" alignItems="center" gap={2} py={3}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading purchase details...
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Products List */}
            {products.length > 0 ? (
              <Box mb={2.5}>
                {products.map((product, idx) => (
                  <ProductItem
                    key={idx}
                    product={product}
                    isLastItem={idx === products.length - 1}
                  />
                ))}
              </Box>
            ) : (
              <Box display="flex" gap={2} mb={2.5}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    flexShrink: 0,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    No Image
                  </Typography>
                </Box>
                <Box flex={1} minWidth={0}>
                  <Typography variant="h6">Product</Typography>
                </Box>
              </Box>
            )}

            {/* Divider */}
            <SectionDivider>
              {/* Date and Points Row */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={cartId ? 2 : 0}
              >
                <DateDisplay date={createdAt} />
                <PointsBadge
                  points={pointsDisplay ?? "—"}
                  label="Redeemed"
                  variant="spent"
                />
              </Box>

              {/* View Details Link */}
              {cartId && (
                <Box display="flex" justifyContent="center">
                  <Box
                    style={{
                      color: "#1976d2",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    View order details →
                  </Box>
                </Box>
              )}
            </SectionDivider>
          </Box>
        )}
      </BaseTransactionCard>
    </Link>
  );
};
